/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React, {
    ReactNode,
    CSSProperties,
    FocusEventHandler,
    InputHTMLAttributes,
    KeyboardEventHandler,
    TextareaHTMLAttributes,
    useEffect,
    useRef,
    useState
} from 'react';
import {enterPressed, ErrorType, generateComponentId, NumberString, OrnamentShape} from '@dvrd/dvr-controls';
import {BluemateControlShape, BluemateControlSize, DateInputConfig} from '../../../util/types';
import BluemateInput from './bluemateInput';

export interface InputProps {
    onChange: (value: string, evt?: React.ChangeEvent) => void;
    onFocus?: FocusEventHandler;
    onBlur?: FocusEventHandler;
    onKeyDown?: KeyboardEventHandler;
    onEnter?: KeyboardEventHandler;
    /** A `NumberString` keeps the raw text the user typed next to its parsed
     *  number, so a comma decimal survives the round trip through this field. */
    value: string | number | NumberString;
    label?: ReactNode;
    ornaments?: OrnamentShape | OrnamentShape[];
    inputProps?: InputHTMLAttributes<any> & TextareaHTMLAttributes<any>;
    disabled?: boolean;
    autoFocus?: boolean;
    autoSelect?: boolean;
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    ornamentClassName?: string;
    errorClassName?: string;
    error?: ErrorType;
    id?: string;
    fullWidth?: boolean;
    active?: boolean;
    area?: boolean;
    unControlled?: boolean;
    allowCharReg?: string | RegExp;
    placeholder?: string;
    style?: CSSProperties;
    size?: BluemateControlSize;
    shape?: BluemateControlShape;
    /** Opt-in modern look for square inputs: 0.5rem radius (same as the
     *  square buttons) + 600 weight. Rounded inputs carry this already. */
    styled?: boolean;
    /** Unified form typography: 0.9rem/500 label, 1rem/500 input text — so
     * mixed controls in one form read as one family. Defaults to false. */
    uniformFont?: boolean;
    /** Delays the `onChange` callback by N ms. The field itself keeps updating
     *  on every keystroke — only the callback waits. */
    debounce?: number;
    /** Shows a clear (×) button while the field holds a value. Clearing emits
     *  an empty value right away, without waiting for `debounce`. */
    clearable?: boolean;
    date?: DateInputConfig | null;
}

function toInputValue(value: string | number | NumberString): string | number {
    return value instanceof NumberString ? value.stringValue : value;
}

export default function InputController(props: InputProps) {
    const {
        label,
        labelClassName,
        ornamentClassName,
        inputClassName,
        errorClassName,
        autoSelect,
        inputProps,
        className,
        id,
        area,
        active,
        autoFocus,
        onFocus,
        unControlled,
        error,
        fullWidth,
        onBlur,
        onChange,
        onKeyDown,
        disabled,
        ornaments,
        onEnter,
        allowCharReg,
        placeholder,
        style,
        size = 'normal',
        shape = 'square',
        styled,
        uniformFont,
        debounce = 0,
        clearable,
        date
    } = props;
    const [value, setValue] = useState<string | number>(toInputValue(props.value));
    const charReg = allowCharReg;
    const componentId = useRef(generateComponentId(id));
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    function _onChange(evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        if (!disabled) {
            const newValue = filterChars(evt.target.value);
            setValue(newValue);
            emitChange(newValue, evt);
        }
    }

    function _onClear() {
        if (disabled) return;
        cancelDebounce();
        setValue('');
        onChange('');
    }

    function _onKeyDown(evt: React.KeyboardEvent) {
        if (!isEditingKey(evt) && !charIsAllowed(evt.key)) return evt.preventDefault();
        if (!disabled) {
            if (onEnter && enterPressed(evt)) onEnter(evt);
            else if (onKeyDown) onKeyDown(evt);
        }
    }

    function emitChange(newValue: string, evt: React.ChangeEvent) {
        cancelDebounce();
        if (debounce <= 0) return onChange(newValue, evt);
        debounceRef.current = setTimeout(() => onChange(newValue, evt), debounce);
    }

    function cancelDebounce() {
        if (!debounceRef.current) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
    }

    function filterChars(value: string) {
        if (!charReg) return value;
        const reg: RegExp = typeof charReg === 'string' ? new RegExp(charReg) : charReg;
        const chars: Array<string> = [];
        for (const char of value) {
            if (reg.test(char)) chars.push(char);
        }
        return chars.join('');
    }

    // Only real character input is filtered. Navigation and editing keys
    // (arrows, Backspace, Home/End, Tab) have multi-character names that no
    // `allowCharReg` matches, so they used to be swallowed by the filter.
    function isEditingKey(evt: React.KeyboardEvent): boolean {
        return evt.key.length > 1 || evt.metaKey || evt.ctrlKey || evt.altKey;
    }

    function charIsAllowed(char: string): boolean {
        if (!charReg) return true;
        const reg = typeof charReg === 'string' ? new RegExp(charReg) : charReg;
        return reg.test(char);
    }

    function _onFocus(evt: React.FocusEvent) {
        if (!disabled && onFocus) onFocus(evt);
    }

    function _onBlur(evt: React.FocusEvent) {
        if (disabled) return;
        if (onBlur) onBlur(evt);
    }

    useEffect(() => {
        if (!unControlled) setValue(toInputValue(props.value));
    }, [props.value]);

    useEffect(
        () => () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        },
        []
    );

    return (
        <BluemateInput
            onChange={_onChange}
            onFocus={_onFocus}
            onKeyDown={_onKeyDown}
            value={value}
            disabled={disabled}
            autoFocus={autoFocus}
            error={error}
            label={label}
            ornaments={ornaments}
            inputProps={inputProps}
            className={className}
            inputClassName={inputClassName}
            labelClassName={labelClassName}
            ornamentClassName={ornamentClassName}
            fullWidth={fullWidth}
            errorClassName={errorClassName}
            id={componentId.current}
            onBlur={_onBlur}
            autoSelect={autoSelect}
            active={active}
            area={area}
            placeholder={placeholder}
            style={style}
            size={size}
            shape={shape}
            styled={styled}
            uniformFont={uniformFont}
            clearable={clearable}
            onClear={_onClear}
            date={date}
        />
    );
}
